// ABOUTME: Helpers that turn a chat message into renderable content
// ABOUTME: Splits out markdown images and renders the rest as markdown

import React from "react"
import Markdown from "markdown-to-jsx"
import { AthletesIcon } from "../icons"
import { ErrorBoundary } from "../components/ErrorBoundary"

// The server may answer with a whole HTML page (a gateway error, for example).
// Parsing it would make React create elements out of tags like `!DOCTYPE html`
// and throw, so raw HTML is shown as plain text instead.
const markdownOptions = { disableParsingRawHTML: true }

export const extractFullUrl = (text: string): { url: string; start: number; end: number } | null => {
  const markdownStart = text.indexOf("![")
  if (markdownStart === -1) return null

  const urlStart = text.indexOf("](", markdownStart)
  if (urlStart === -1) return null

  const start = urlStart + 2
  let openBraces = 0
  let end = start

  for (let i = start; i < text.length; i++) {
    if (text[i] === "{") {
      openBraces++
    } else if (text[i] === "}") {
      openBraces--
    } else if (text[i] === ")" && openBraces === 0) {
      end = i
      break
    }
  }

  return {
    url: text.substring(start, end),
    start: markdownStart,
    end: end + 1,
  }
}

export const renderMessageContent = (
  text: string,
  mentionedAthleteIDs?: readonly string[] | null,
  isCoach?: boolean,
  onAthleteClick?: (athleteIds: readonly string[]) => void,
  t?: (key: string) => string,
) => {
  const parts = []
  let currentIndex = 0
  let currentText = text

  while (currentText) {
    const imageInfo = extractFullUrl(currentText)

    if (!imageInfo) {
      parts.push(
        <Markdown
          key={currentIndex}
          options={markdownOptions}
        >
          {currentText}
        </Markdown>,
      )
      break
    }

    if (imageInfo.start > 0) {
      parts.push(
        <Markdown
          key={`text-${currentIndex}`}
          options={markdownOptions}
        >
          {currentText.substring(0, imageInfo.start)}
        </Markdown>,
      )
    }

    parts.push(
      <img
        key={`img-${currentIndex}`}
        src={imageInfo.url}
        alt="Chart"
        className="max-w-full h-auto rounded-lg my-2"
        loading="lazy"
      />,
    )

    currentText = currentText.substring(imageInfo.end)
    currentIndex++
  }

  // Wrap content with click handler if there are mentioned athletes (only for coaches)
  const content =
    mentionedAthleteIDs && mentionedAthleteIDs.length > 0 && isCoach && onAthleteClick ? (
      <button
        type="button"
        className="w-full text-left cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
        onClick={() => onAthleteClick(mentionedAthleteIDs)}
        title={t?.("chat.clickToViewAthletes")}
      >
        {parts}
        <div className="text-xs text-blue-600 mt-2 flex items-center gap-1">
          <AthletesIcon className="w-4 h-4" />
          {t?.("chat.athletesMentioned")}: {mentionedAthleteIDs.length}
        </div>
      </button>
    ) : (
      <>{parts}</>
    )

  // A single unrenderable message must not take the whole app down with it.
  return (
    <ErrorBoundary
      resetKey={text}
      fallback={<p className="text-sm text-red-700">{t?.("chat.errorOccurred")}</p>}
    >
      {content}
    </ErrorBoundary>
  )
}

export const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString()
}
